import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../services/api';

// Async thunk for creating session
export const createSession = createAsyncThunk(
  'onboarding/createSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/onboarding/sessions/');
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: 'Failed to create session. Please try again.' });
    }
  }
);

// Async thunk for fetching session details
export const fetchSessionDetails = createAsyncThunk(
  'onboarding/fetchSessionDetails',
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/onboarding/sessions/${sessionId}/`);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: 'Failed to fetch session details. Please try again.' });
    }
  }
);

// Async thunk for updating session with email
export const updateSessionEmail = createAsyncThunk(
  'onboarding/updateSessionEmail',
  async ({ sessionId, email }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/onboarding/sessions/${sessionId}/`, { email });
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: 'Failed to update session. Please try again.' });
    }
  }
);


// Async thunk for saving selected features to session
export const saveSelectedFeatures = createAsyncThunk(
  'onboarding/saveSelectedFeatures',
  async ({ sessionId, features }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/onboarding/sessions/${sessionId}/`, {
        additional_data: { selected_features: features }
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: 'Failed to save features. Please try again.' });
    }
  }
);

// Async thunk for creating Keycloak user
export const createKeycloakUser = createAsyncThunk(
  'onboarding/createKeycloakUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/onboarding/user/', userData);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: 'Failed to create user. Please try again.' });
    }
  }
);

const initialState = {
  sessionId: null,
  isCreatingSession: false,
  sessionError: null,
  sessionCreated: false,
  sessionData: null,
  isFetchingSession: false,
  fetchSessionError: null,
  isUpdatingSession: false,
  updateSessionError: null,
  isAuthenticated: false,
  authenticatedUser: null,
  authTokens: null,
  organization: null,
  isCreatingOrganization: false,
  organizationError: null,
  organizationCreated: false,
  user: null,
  isCreatingUser: false,
  userError: null,
  userCreated: false,
  features: null,
  isLoadingFeatures: false,
  featuresError: null,
  isSavingFeatures: false,
  saveFeaturesError: null,
  featuresSaved: false,
  dashboardData: null,
  isLoadingDashboard: false,
  dashboardError: null,
  ehrCredentials: null,
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    // Reset session state
    resetSessionState: (state) => {
      state.sessionId = null;
      state.isCreatingSession = false;
      state.sessionError = null;
      state.sessionCreated = false;
    },
    
    // Clear session error
    clearSessionError: (state) => {
      state.sessionError = null;
    },
    
    // Clear fetch session error
    clearFetchSessionError: (state) => {
      state.fetchSessionError = null;
    },
    
    // Clear update session error
    clearUpdateSessionError: (state) => {
      state.updateSessionError = null;
    },
    
    // Set authentication state
    setAuthentication: (state, action) => {
      state.isAuthenticated = true;
      state.authenticatedUser = action.payload.user;
      state.authTokens = action.payload.tokens;
    },
    
    // Clear authentication state
    clearAuthentication: (state) => {
      state.isAuthenticated = false;
      state.authenticatedUser = null;
      state.authTokens = null;
    },
    
    // Update session ID (for verification flow)
    updateSessionId: (state, action) => {
      state.sessionId = action.payload;
    },
    
    // Set organization ID
    setOrganizationId: (state, action) => {
      state.organization = { id: action.payload };
    },
    
    // Reset organization state
    resetOrganizationState: (state) => {
      state.organization = null;
      state.isCreatingOrganization = false;
      state.organizationError = null;
      state.organizationCreated = false;
    },
    
    // Clear organization error
    clearOrganizationError: (state) => {
      state.organizationError = null;
    },
    
    // Reset user state
    resetUserState: (state) => {
      state.user = null;
      state.isCreatingUser = false;
      state.userError = null;
      state.userCreated = false;
    },
    
    // Clear user error
    clearUserError: (state) => {
      state.userError = null;
    },
    
    // Reset features state
    resetFeaturesState: (state) => {
      state.features = null;
      state.isLoadingFeatures = false;
      state.featuresError = null;
    },
    
    // Clear features error
    clearFeaturesError: (state) => {
      state.featuresError = null;
    },
    
    // Features reducers (for local API calls)
    setFeaturesLoading: (state, action) => {
      state.isLoadingFeatures = action.payload;
    },
    
    setFeaturesData: (state, action) => {
      state.features = action.payload;
      state.featuresError = null;
    },
    
    setFeaturesError: (state, action) => {
      state.featuresError = action.payload;
    },
    
    // Reset save features state
    resetSaveFeaturesState: (state) => {
      state.isSavingFeatures = false;
      state.saveFeaturesError = null;
      state.featuresSaved = false;
    },
    
    // Clear save features error
    clearSaveFeaturesError: (state) => {
      state.saveFeaturesError = null;
    },
    
    // Dashboard reducers (for local API calls)
    setDashboardLoading: (state, action) => {
      state.isLoadingDashboard = action.payload;
    },
    
    setDashboardData: (state, action) => {
      state.dashboardData = action.payload;
      state.dashboardError = null;
    },
    
    setDashboardError: (state, action) => {
      state.dashboardError = action.payload;
    },
    
    clearDashboardError: (state) => {
      state.dashboardError = null;
    },
    
    resetDashboardState: (state) => {
      state.dashboardData = null;
      state.isLoadingDashboard = false;
      state.dashboardError = null;
    },
    
    // EHR Credentials reducer
    setEhrCredentials: (state, action) => {
      state.ehrCredentials = action.payload;
    },
    
    clearEhrCredentials: (state) => {
      state.ehrCredentials = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create session
      .addCase(createSession.pending, (state) => {
        state.isCreatingSession = true;
        state.sessionError = null;
        state.sessionCreated = false;
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.isCreatingSession = false;
        state.sessionId = action.payload.session_id;
        state.sessionCreated = true;
        state.sessionError = null;
      })
      .addCase(createSession.rejected, (state, action) => {
        state.isCreatingSession = false;
        state.sessionError = action.payload;
        state.sessionCreated = false;
      })
      // Fetch session details
      .addCase(fetchSessionDetails.pending, (state) => {
        state.isFetchingSession = true;
        state.fetchSessionError = null;
      })
      .addCase(fetchSessionDetails.fulfilled, (state, action) => {
        state.isFetchingSession = false;
        state.sessionData = action.payload.session;
        state.fetchSessionError = null;
      })
      .addCase(fetchSessionDetails.rejected, (state, action) => {
        state.isFetchingSession = false;
        state.fetchSessionError = action.payload;
      })
      // Update session with email
      .addCase(updateSessionEmail.pending, (state) => {
        state.isUpdatingSession = true;
        state.updateSessionError = null;
      })
      .addCase(updateSessionEmail.fulfilled, (state, action) => {
        state.isUpdatingSession = false;
        state.sessionData = action.payload.session;
        state.updateSessionError = null;
      })
      .addCase(updateSessionEmail.rejected, (state, action) => {
        state.isUpdatingSession = false;
        state.updateSessionError = action.payload;
      })
      // Create Keycloak user
      .addCase(createKeycloakUser.pending, (state) => {
        state.isCreatingUser = true;
        state.userError = null;
        state.userCreated = false;
      })
      .addCase(createKeycloakUser.fulfilled, (state, action) => {
        state.isCreatingUser = false;
        state.user = action.payload;
        state.userCreated = true;
        state.userError = null;
      })
      .addCase(createKeycloakUser.rejected, (state, action) => {
        state.isCreatingUser = false;
        state.userError = action.payload;
        state.userCreated = false;
      })
      // Save selected features
      .addCase(saveSelectedFeatures.pending, (state) => {
        state.isSavingFeatures = true;
        state.saveFeaturesError = null;
        state.featuresSaved = false;
      })
      .addCase(saveSelectedFeatures.fulfilled, (state, action) => {
        state.isSavingFeatures = false;
        state.saveFeaturesError = null;
        state.featuresSaved = true;
      })
      .addCase(saveSelectedFeatures.rejected, (state, action) => {
        state.isSavingFeatures = false;
        state.saveFeaturesError = action.payload;
        state.featuresSaved = false;
      });
  },
});

// Action creators
export const {
  resetSessionState,
  clearSessionError,
  clearFetchSessionError,
  clearUpdateSessionError,
  setAuthentication,
  clearAuthentication,
  updateSessionId,
  setOrganizationId,
  resetOrganizationState,
  clearOrganizationError,
  resetUserState,
  clearUserError,
  resetFeaturesState,
  clearFeaturesError,
  setFeaturesLoading,
  setFeaturesData,
  setFeaturesError,
  resetSaveFeaturesState,
  clearSaveFeaturesError,
  setDashboardLoading,
  setDashboardData,
  setDashboardError,
  clearDashboardError,
  resetDashboardState,
  setEhrCredentials,
  clearEhrCredentials,
} = onboardingSlice.actions;

// Selectors
export const selectSessionId = (state) => state.onboarding.sessionId;
export const selectIsCreatingSession = (state) => state.onboarding.isCreatingSession;
export const selectSessionError = (state) => state.onboarding.sessionError;
export const selectSessionCreated = (state) => state.onboarding.sessionCreated;
export const selectSessionData = (state) => state.onboarding.sessionData;
export const selectIsFetchingSession = (state) => state.onboarding.isFetchingSession;
export const selectFetchSessionError = (state) => state.onboarding.fetchSessionError;
export const selectIsUpdatingSession = (state) => state.onboarding.isUpdatingSession;
export const selectUpdateSessionError = (state) => state.onboarding.updateSessionError;
export const selectIsAuthenticated = (state) => state.onboarding.isAuthenticated;
export const selectAuthenticatedUser = (state) => state.onboarding.authenticatedUser;
export const selectAuthTokens = (state) => state.onboarding.authTokens;
export const selectOrganization = (state) => state.onboarding.organization;
export const selectIsCreatingOrganization = (state) => state.onboarding.isCreatingOrganization;
export const selectOrganizationError = (state) => state.onboarding.organizationError;
export const selectOrganizationCreated = (state) => state.onboarding.organizationCreated;
export const selectUser = (state) => state.onboarding.user;
export const selectIsCreatingUser = (state) => state.onboarding.isCreatingUser;
export const selectUserError = (state) => state.onboarding.userError;
export const selectUserCreated = (state) => state.onboarding.userCreated;
export const selectFeatures = (state) => state.onboarding.features;
export const selectIsLoadingFeatures = (state) => state.onboarding.isLoadingFeatures;
export const selectFeaturesError = (state) => state.onboarding.featuresError;
export const selectOrganizationId = (state) => state.onboarding.organization?.id;
export const selectIsSavingFeatures = (state) => state.onboarding.isSavingFeatures;
export const selectSaveFeaturesError = (state) => state.onboarding.saveFeaturesError;
export const selectFeaturesSaved = (state) => state.onboarding.featuresSaved;
export const selectDashboardData = (state) => state.onboarding.dashboardData;
export const selectIsLoadingDashboard = (state) => state.onboarding.isLoadingDashboard;
export const selectDashboardError = (state) => state.onboarding.dashboardError;
export const selectEhrCredentials = (state) => state.onboarding.ehrCredentials;

export default onboardingSlice.reducer;
